import React from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import TipTapEditor from "../../../TipTap/TipTapEditor";
import ModelSelectField from "../../../common/ModelSelectField";
import {
  REASONING_EFFORT_AUTO,
  REASONING_EFFORT_OPTIONS,
  supportsReasoningEffort,
} from "../../../../utils/ModelConfig";
import { COMPLETION_NOTIFICATION_TYPES } from "../emailPolicy";
import {
  SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
  SELF_CONFIGURATION_MODE_AWARENESS_ONLY,
  SELF_CONFIGURATION_MODE_OFF,
  ensureSelfConfigurationToolsForMode,
  getSelfConfigurationModeHelpText,
  isSelfConfigurationAwarenessEnabled,
  isSelfConfigurationEditEnabled,
} from "../selfConfiguration";
import { ensureTaskQueuePlannerToolsPaired } from "../toolCatalog";

export default function GeneralTab(props) {
  const {
    isSystemAgent,
    name,
    setName,
    description,
    setDescription,
    systemPrompt,
    setSystemPrompt,
    model,
    setModel,
    reasoningEffort,
    setReasoningEffort,
    maxIterations,
    setMaxIterations,
    selfConfigurationMode,
    setSelfConfigurationMode,
    setEnabledTools,
    completionNotificationType,
    setCompletionNotificationType,
    completionNotifyEmails,
    setCompletionNotifyEmails,
    completionSlackWebhookUrl,
    setCompletionSlackWebhookUrl,
    completionWebhookUrl,
    setCompletionWebhookUrl,
    globalVarPlaceholders,
    allAgents,
    webhookPayloadPreview,
  } = props;

  return (
    <>
      {isSystemAgent && (
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            This is a system agent. Only the model can be changed.
          </Typography>
        </Box>
      )}
      {/* Details */}
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary", mb: 2 }}
      >
        Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography className="org-field-label">Name</Typography>
          <TextField
            className="org-styled-textfield"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Agent"
            disabled={isSystemAgent}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className="org-field-label">Description</Typography>
          <TextField
            className="org-styled-textfield"
            fullWidth
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A brief description of what this agent does"
            disabled={isSystemAgent}
          />
        </Grid>
        {!isSystemAgent && (
          <Grid item xs={12}>
            <Typography className="org-field-label">
              Self Configuration
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                p: 2,
                backgroundColor: "action.hover",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 0.5,
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: "100%", display: "flex" }}
                  control={
                    <Checkbox
                      checked={isSelfConfigurationAwarenessEnabled(
                        selfConfigurationMode
                      )}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        const nextMode = !enabled
                          ? SELF_CONFIGURATION_MODE_OFF
                          : isSelfConfigurationEditEnabled(
                              selfConfigurationMode
                            )
                          ? SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
                          : SELF_CONFIGURATION_MODE_AWARENESS_ONLY;
                        setSelfConfigurationMode(nextMode);
                        setEnabledTools((prev) =>
                          ensureSelfConfigurationToolsForMode(
                            ensureTaskQueuePlannerToolsPaired(prev),
                            nextMode
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VisibilityOutlinedIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        sx={{ fontSize: "13px", color: "text.primary" }}
                      >
                        Enable self-configuration awareness
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  sx={{ m: 0, width: "100%", display: "flex" }}
                  control={
                    <Checkbox
                      checked={isSelfConfigurationEditEnabled(
                        selfConfigurationMode
                      )}
                      disabled={
                        !isSelfConfigurationAwarenessEnabled(
                          selfConfigurationMode
                        )
                      }
                      onChange={(e) => {
                        const nextMode = e.target.checked
                          ? SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
                          : SELF_CONFIGURATION_MODE_AWARENESS_ONLY;
                        setSelfConfigurationMode(nextMode);
                        setEnabledTools((prev) =>
                          ensureSelfConfigurationToolsForMode(
                            ensureTaskQueuePlannerToolsPaired(prev),
                            nextMode
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EditOutlinedIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        sx={{ fontSize: "13px", color: "text.primary" }}
                      >
                        Allow agent to edit its own config
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Typography
                sx={{ fontSize: "11px", color: "text.secondary", mt: 1 }}
              >
                {getSelfConfigurationModeHelpText(selfConfigurationMode)}
              </Typography>
            </Box>
          </Grid>
        )}
        {!isSystemAgent && (
          <Grid item xs={12}>
            <Typography className="org-field-label">System Prompt</Typography>
            <TipTapEditor
              value={systemPrompt}
              onChange={setSystemPrompt}
              placeholder="You are a helpful agent that..."
              markdown
              showToolbar={true}
              minHeight="160px"
              triggerChars={["$", "@"]}
              availablePlaceholders={globalVarPlaceholders}
              agentMentions={allAgents.map((a) => ({
                key: a.name,
                type: "agent",
                id: a.agentId,
                description: a.description,
              }))}
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
            >
              Instructions that define the agent's behavior. Type @ to reference
              other agents, $ for placeholders.
            </Typography>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      {/* Model & Limits */}
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary", mb: 2 }}
      >
        Model & Limits
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={supportsReasoningEffort(model) ? 5 : 8}>
          <Typography className="org-field-label">Model</Typography>
          <ModelSelectField
            className="org-styled-textfield"
            fullWidth
            size="small"
            value={model}
            onChange={(nextModel) => {
              setModel(nextModel);
              if (!supportsReasoningEffort(nextModel)) {
                setReasoningEffort(REASONING_EFFORT_AUTO);
              }
            }}
          />
        </Grid>
        {supportsReasoningEffort(model) && (
          <Grid item xs={12} sm={3}>
            <Typography className="org-field-label">Reasoning Effort</Typography>
            <TextField
              className="org-styled-textfield"
              select
              fullWidth
              size="small"
              value={reasoningEffort || REASONING_EFFORT_AUTO}
              onChange={(e) => setReasoningEffort(e.target.value)}
            >
              {REASONING_EFFORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        {!isSystemAgent && (
          <Grid item xs={12} sm={4}>
            <Typography className="org-field-label">Max Iterations</Typography>
            <TextField
              className="org-styled-textfield"
              type="number"
              fullWidth
              size="small"
              value={maxIterations}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 200)
                  setMaxIterations(val);
              }}
              inputProps={{ min: 1, max: 200 }}
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
            >
              Per execution (1-200).
            </Typography>
          </Grid>
        )}
      </Grid>

      {!isSystemAgent && (
        <>
          <Divider sx={{ borderColor: "divider", my: 2.5 }} />

          {/* Completion Notification */}
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Completion Notification
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
            Notify when the agent has finished responding.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="org-field-label">
                Notification Channel
              </Typography>
              <TextField
                className="org-styled-textfield"
                select
                fullWidth
                size="small"
                value={completionNotificationType}
                onChange={(e) => setCompletionNotificationType(e.target.value)}
              >
                {COMPLETION_NOTIFICATION_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {completionNotificationType === "EMAIL" && (
              <Grid item xs={12}>
                <Typography className="org-field-label">
                  Email Recipients
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={completionNotifyEmails}
                  onChange={(e) => setCompletionNotifyEmails(e.target.value)}
                  placeholder="name@example.com, ops@example.com"
                />
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
                >
                  Comma-separated email addresses.
                </Typography>
              </Grid>
            )}

            {completionNotificationType === "SLACK" && (
              <Grid item xs={12}>
                <Typography className="org-field-label">
                  Slack Webhook URL
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={completionSlackWebhookUrl}
                  onChange={(e) => setCompletionSlackWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                />
              </Grid>
            )}

            {completionNotificationType === "WEBHOOK" && (
              <Grid item xs={12}>
                <Typography className="org-field-label">
                  Webhook URL (POST)
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={completionWebhookUrl}
                  onChange={(e) => setCompletionWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhooks/agent"
                />
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
                >
                  Receives a JSON payload with session details and direct links
                  to the chat.
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      px: 1.5,
                      py: 0.75,
                      backgroundColor: "action.hover",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    Payload Preview
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      fontSize: "11px",
                      lineHeight: 1.45,
                      overflowX: "auto",
                      backgroundColor: "background.paper",
                      color: "text.secondary",
                    }}
                  >
                    {JSON.stringify(webhookPayloadPreview, null, 2)}
                  </Box>
                </Box>
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mt: 0.75 }}
                >
                  `workflowSession` link is included when the agent run is tied
                  to a workflow session.
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
}
